/**
 * Mock whānau report profiles — DEV/DEMO ONLY.
 *
 * 40 records reproduced from docs/Sample analysis.xlsx ('Mock raw data'), remapped
 * to the canonical 18-subdomain framework (src/framework/data.ts):
 *   • workbook 'Parent education'  → 'adult-literacy'  (renamed per Sue 2026-05-11)
 *   • workbook 'Housing/accommodation' → 'accommodation'
 *   • 'poverty' (18th subdomain, added after the workbook) has no historical data,
 *     so it is 'not-assessed' for every whānau — it therefore contributes nothing to
 *     any roll-up, and the Pou/domain totals still match the workbook exactly.
 *
 * FAITHFUL: factor states, ethnicity, iwi and location come straight from the workbook
 * (place/iwi names corrected to proper macrons). SYNTHESISED: `service` and
 * `durationBand` were blank in the mock and are assigned deterministically here purely
 * so the cohort filters are demonstrable. None of this ships to production — the live
 * dashboard reads de-identified aggregates from the API (see useReportData.ts).
 */
import type {
  DefaultSubdomainId,
  FactorStateKind,
} from "../../framework/types";
import type { DurationBand, ReportProfile } from "../reportTypes";

/** Column order for the compact ENTRY/EXIT arrays below. */
export const MOCK_SUBDOMAIN_ORDER: readonly DefaultSubdomainId[] = [
  "cultural-connection",
  "spiritual-connection",
  "social-connection",
  "relationships",
  "support-networks",
  "home-environment",
  "parenting",
  "adult-literacy",
  "child-education",
  "mental-health",
  "physical-health",
  "emotional-health-behaviour",
  "addictions",
  "disability",
  "employment",
  "financial-stability",
  "accommodation",
  "poverty",
];

type Row = {
  id: number;
  household: number | null;
  ethnicity: string | null;
  iwi: string | null;
  location: string | null;
  service: string | null;
  durationBand: DurationBand | null;
  /** Factor state per subdomain at entry, aligned to MOCK_SUBDOMAIN_ORDER. */
  entry: FactorStateKind[];
  /** Factor state per subdomain at exit, aligned to MOCK_SUBDOMAIN_ORDER. */
  exit: FactorStateKind[];
};

const ROWS: Row[] = [
  {
    id: 1,
    household: 2,
    ethnicity: "Māori",
    iwi: "Ngāti Awa",
    location: "Ōpōtiki",
    service: "Health Navigator",
    durationBand: "6-12m",
    entry: [
      "risk",
      "risk",
      "change",
      "protective",
      "change",
      "risk",
      "change",
      "change",
      "protective",
      "change",
      "change",
      "change",
      "risk",
      "not-assessed",
      "change",
      "change",
      "risk",
      "not-assessed",
    ],
    exit: [
      "risk",
      "risk",
      "protective",
      "protective",
      "protective",
      "protective",
      "change",
      "change",
      "protective",
      "change",
      "change",
      "protective",
      "change",
      "not-assessed",
      "change",
      "change",
      "protective",
      "not-assessed",
    ],
  },
  {
    id: 2,
    household: 3,
    ethnicity: "Māori",
    iwi: "Ngāti Awa",
    location: "Ōpōtiki",
    service: "Rukuhia Te Mahi",
    durationBand: "1-2y",
    entry: [
      "risk",
      "risk",
      "change",
      "protective",
      "protective",
      "change",
      "risk",
      "protective",
      "change",
      "change",
      "change",
      "protective",
      "not-assessed",
      "not-assessed",
      "protective",
      "protective",
      "change",
      "not-assessed",
    ],
    exit: [
      "risk",
      "risk",
      "protective",
      "protective",
      "protective",
      "protective",
      "risk",
      "protective",
      "change",
      "change",
      "change",
      "protective",
      "not-assessed",
      "not-assessed",
      "protective",
      "protective",
      "change",
      "not-assessed",
    ],
  },
  {
    id: 3,
    household: 5,
    ethnicity: "Māori",
    iwi: "Ngāti Awa",
    location: "Ōpōtiki",
    service: "Tamariki Ora",
    durationBand: "over-2y",
    entry: [
      "change",
      "risk",
      "change",
      "risk",
      "change",
      "risk",
      "risk",
      "change",
      "change",
      "protective",
      "protective",
      "protective",
      "not-assessed",
      "not-assessed",
      "change",
      "protective",
      "change",
      "not-assessed",
    ],
    exit: [
      "change",
      "change",
      "protective",
      "risk",
      "protective",
      "change",
      "risk",
      "change",
      "protective",
      "protective",
      "protective",
      "protective",
      "not-assessed",
      "not-assessed",
      "change",
      "protective",
      "protective",
      "not-assessed",
    ],
  },
  {
    id: 4,
    household: 7,
    ethnicity: "Māori",
    iwi: "Ngā Puhi",
    location: "Ōpōtiki",
    service: "He Tohu Kāinga",
    durationBand: "under-6m",
    entry: [
      "risk",
      "risk",
      "protective",
      "risk",
      "risk",
      "change",
      "risk",
      "risk",
      "risk",
      "risk",
      "protective",
      "change",
      "not-assessed",
      "not-assessed",
      "protective",
      "protective",
      "protective",
      "not-assessed",
    ],
    exit: [
      "risk",
      "change",
      "protective",
      "change",
      "change",
      "change",
      "risk",
      "change",
      "change",
      "risk",
      "protective",
      "change",
      "not-assessed",
      "not-assessed",
      "protective",
      "protective",
      "protective",
      "not-assessed",
    ],
  },
  {
    id: 5,
    household: 2,
    ethnicity: "Māori",
    iwi: "Te Rarawa",
    location: "Ōpōtiki",
    service: "Transition to Adulthood",
    durationBand: "6-12m",
    entry: [
      "risk",
      "change",
      "protective",
      "risk",
      "protective",
      "change",
      "change",
      "risk",
      "risk",
      "change",
      "risk",
      "change",
      "not-assessed",
      "not-assessed",
      "change",
      "change",
      "change",
      "not-assessed",
    ],
    exit: [
      "change",
      "change",
      "protective",
      "risk",
      "protective",
      "protective",
      "change",
      "change",
      "protective",
      "change",
      "risk",
      "protective",
      "not-assessed",
      "not-assessed",
      "change",
      "change",
      "protective",
      "not-assessed",
    ],
  },
  {
    id: 6,
    household: 3,
    ethnicity: "Māori",
    iwi: "Tūhoe",
    location: "Ōpōtiki",
    service: "Family Start",
    durationBand: "1-2y",
    entry: [
      "risk",
      "change",
      "protective",
      "risk",
      "change",
      "risk",
      "risk",
      "risk",
      "change",
      "change",
      "risk",
      "change",
      "not-assessed",
      "change",
      "protective",
      "risk",
      "change",
      "not-assessed",
    ],
    exit: [
      "change",
      "change",
      "protective",
      "change",
      "change",
      "change",
      "change",
      "risk",
      "protective",
      "change",
      "change",
      "change",
      "not-assessed",
      "protective",
      "protective",
      "risk",
      "change",
      "not-assessed",
    ],
  },
  {
    id: 7,
    household: 3,
    ethnicity: "Cook Island",
    iwi: null,
    location: "Ōpōtiki",
    service: "Social Workers in Schools",
    durationBand: "over-2y",
    entry: [
      "change",
      "risk",
      "change",
      "risk",
      "change",
      "change",
      "change",
      "change",
      "change",
      "protective",
      "protective",
      "change",
      "not-assessed",
      "not-assessed",
      "change",
      "change",
      "change",
      "not-assessed",
    ],
    exit: [
      "change",
      "change",
      "protective",
      "change",
      "change",
      "protective",
      "change",
      "change",
      "protective",
      "protective",
      "protective",
      "protective",
      "not-assessed",
      "not-assessed",
      "change",
      "change",
      "change",
      "not-assessed",
    ],
  },
  {
    id: 8,
    household: 4,
    ethnicity: "Māori",
    iwi: "Tūhoe",
    location: "Ōpōtiki",
    service: "Whānau Resilience",
    durationBand: "under-6m",
    entry: [
      "change",
      "risk",
      "change",
      "risk",
      "change",
      "risk",
      "change",
      "protective",
      "risk",
      "protective",
      "change",
      "risk",
      "risk",
      "not-assessed",
      "protective",
      "change",
      "risk",
      "not-assessed",
    ],
    exit: [
      "change",
      "change",
      "protective",
      "change",
      "protective",
      "change",
      "change",
      "protective",
      "change",
      "protective",
      "change",
      "risk",
      "risk",
      "not-assessed",
      "protective",
      "change",
      "change",
      "not-assessed",
    ],
  },
  {
    id: 9,
    household: 4,
    ethnicity: "Māori",
    iwi: "Ngāti Awa",
    location: "Whakatāne",
    service: "Health Navigator",
    durationBand: "6-12m",
    entry: [
      "risk",
      "risk",
      "risk",
      "change",
      "risk",
      "protective",
      "change",
      "change",
      "change",
      "risk",
      "protective",
      "risk",
      "risk",
      "not-assessed",
      "risk",
      "risk",
      "change",
      "not-assessed",
    ],
    exit: [
      "change",
      "change",
      "change",
      "change",
      "change",
      "protective",
      "change",
      "change",
      "protective",
      "risk",
      "protective",
      "change",
      "change",
      "not-assessed",
      "risk",
      "risk",
      "protective",
      "not-assessed",
    ],
  },
  {
    id: 10,
    household: 5,
    ethnicity: "Māori / Pākehā",
    iwi: "Ngāti Awa",
    location: "Whakatāne",
    service: "Rukuhia Te Mahi",
    durationBand: "1-2y",
    entry: [
      "change",
      "change",
      "change",
      "risk",
      "protective",
      "change",
      "change",
      "protective",
      "not-assessed",
      "change",
      "change",
      "risk",
      "change",
      "not-assessed",
      "risk",
      "change",
      "change",
      "not-assessed",
    ],
    exit: [
      "change",
      "protective",
      "protective",
      "protective",
      "protective",
      "protective",
      "protective",
      "protective",
      "protective",
      "protective",
      "protective",
      "protective",
      "change",
      "not-assessed",
      "protective",
      "protective",
      "protective",
      "not-assessed",
    ],
  },
  {
    id: 11,
    household: 3,
    ethnicity: "Māori",
    iwi: "Ngāti Awa",
    location: "Whakatāne",
    service: "Tamariki Ora",
    durationBand: "over-2y",
    entry: [
      "change",
      "risk",
      "risk",
      "risk",
      "protective",
      "change",
      "risk",
      "change",
      "change",
      "risk",
      "change",
      "risk",
      "risk",
      "not-assessed",
      "change",
      "protective",
      "risk",
      "not-assessed",
    ],
    exit: [
      "change",
      "risk",
      "change",
      "change",
      "protective",
      "change",
      "change",
      "change",
      "change",
      "risk",
      "change",
      "protective",
      "change",
      "not-assessed",
      "change",
      "protective",
      "protective",
      "not-assessed",
    ],
  },
  {
    id: 12,
    household: 6,
    ethnicity: "Māori",
    iwi: "Ngāti Awa",
    location: "Whakatāne",
    service: "He Tohu Kāinga",
    durationBand: "under-6m",
    entry: [
      "change",
      "risk",
      "risk",
      "protective",
      "change",
      "protective",
      "risk",
      "risk",
      "change",
      "protective",
      "protective",
      "change",
      "not-assessed",
      "not-assessed",
      "risk",
      "risk",
      "change",
      "not-assessed",
    ],
    exit: [
      "protective",
      "protective",
      "change",
      "protective",
      "change",
      "protective",
      "change",
      "change",
      "protective",
      "protective",
      "protective",
      "change",
      "not-assessed",
      "not-assessed",
      "risk",
      "risk",
      "change",
      "not-assessed",
    ],
  },
  {
    id: 13,
    household: 5,
    ethnicity: "Māori",
    iwi: "Ngāti Porou",
    location: "Ōpōtiki",
    service: "Transition to Adulthood",
    durationBand: "6-12m",
    entry: [
      "change",
      "change",
      "risk",
      "risk",
      "protective",
      "change",
      "risk",
      "risk",
      "change",
      "change",
      "change",
      "change",
      "not-assessed",
      "not-assessed",
      "risk",
      "risk",
      "risk",
      "not-assessed",
    ],
    exit: [
      "protective",
      "protective",
      "change",
      "protective",
      "protective",
      "protective",
      "change",
      "risk",
      "change",
      "change",
      "protective",
      "change",
      "not-assessed",
      "not-assessed",
      "risk",
      "risk",
      "risk",
      "not-assessed",
    ],
  },
  {
    id: 14,
    household: 3,
    ethnicity: "Māori",
    iwi: "Ngāti Porou",
    location: "Ōpōtiki",
    service: "Family Start",
    durationBand: "1-2y",
    entry: [
      "protective",
      "protective",
      "protective",
      "risk",
      "change",
      "change",
      "risk",
      "change",
      "risk",
      "change",
      "change",
      "protective",
      "not-assessed",
      "protective",
      "change",
      "risk",
      "protective",
      "not-assessed",
    ],
    exit: [
      "protective",
      "protective",
      "protective",
      "protective",
      "protective",
      "change",
      "change",
      "change",
      "change",
      "change",
      "protective",
      "protective",
      "not-assessed",
      "protective",
      "change",
      "risk",
      "protective",
      "not-assessed",
    ],
  },
  {
    id: 15,
    household: 6,
    ethnicity: "Pākehā",
    iwi: null,
    location: "Whakatāne",
    service: "Social Workers in Schools",
    durationBand: "over-2y",
    entry: [
      "risk",
      "change",
      "change",
      "risk",
      "change",
      "change",
      "risk",
      "risk",
      "change",
      "risk",
      "risk",
      "protective",
      "not-assessed",
      "not-assessed",
      "risk",
      "risk",
      "change",
      "not-assessed",
    ],
    exit: [
      "change",
      "protective",
      "protective",
      "protective",
      "change",
      "change",
      "change",
      "change",
      "protective",
      "change",
      "change",
      "protective",
      "not-assessed",
      "not-assessed",
      "risk",
      "risk",
      "protective",
      "not-assessed",
    ],
  },
  {
    id: 16,
    household: 7,
    ethnicity: "Māori",
    iwi: "Ngāti Awa",
    location: "Whakatāne",
    service: "Whānau Resilience",
    durationBand: "under-6m",
    entry: [
      "protective",
      "protective",
      "protective",
      "change",
      "risk",
      "change",
      "risk",
      "change",
      "risk",
      "change",
      "risk",
      "change",
      "not-assessed",
      "change",
      "risk",
      "risk",
      "protective",
      "not-assessed",
    ],
    exit: [
      "protective",
      "protective",
      "protective",
      "protective",
      "change",
      "protective",
      "change",
      "protective",
      "change",
      "change",
      "change",
      "protective",
      "not-assessed",
      "change",
      "risk",
      "risk",
      "protective",
      "not-assessed",
    ],
  },
  {
    id: 17,
    household: 4,
    ethnicity: "Māori",
    iwi: "Ngāti Awa",
    location: "Whakatāne",
    service: "Health Navigator",
    durationBand: "6-12m",
    entry: [
      "risk",
      "change",
      "protective",
      "risk",
      "risk",
      "change",
      "risk",
      "risk",
      "protective",
      "risk",
      "change",
      "change",
      "not-assessed",
      "not-assessed",
      "risk",
      "risk",
      "risk",
      "not-assessed",
    ],
    exit: [
      "change",
      "protective",
      "protective",
      "risk",
      "risk",
      "protective",
      "risk",
      "risk",
      "protective",
      "protective",
      "change",
      "protective",
      "not-assessed",
      "not-assessed",
      "risk",
      "risk",
      "change",
      "not-assessed",
    ],
  },
  {
    id: 18,
    household: 3,
    ethnicity: "Māori",
    iwi: "Ngāti Awa",
    location: "Whakatāne",
    service: "Rukuhia Te Mahi",
    durationBand: "1-2y",
    entry: [
      "risk",
      "change",
      "change",
      "risk",
      "change",
      "protective",
      "risk",
      "change",
      "risk",
      "risk",
      "risk",
      "change",
      "not-assessed",
      "risk",
      "risk",
      "risk",
      "risk",
      "not-assessed",
    ],
    exit: [
      "change",
      "protective",
      "change",
      "risk",
      "protective",
      "protective",
      "risk",
      "change",
      "protective",
      "protective",
      "change",
      "change",
      "not-assessed",
      "change",
      "risk",
      "protective",
      "risk",
      "not-assessed",
    ],
  },
  {
    id: 19,
    household: 5,
    ethnicity: "Māori",
    iwi: "Ngāti Awa",
    location: "Whakatāne",
    service: "Tamariki Ora",
    durationBand: "over-2y",
    entry: [
      "risk",
      "risk",
      "change",
      "risk",
      "risk",
      "change",
      "protective",
      "change",
      "risk",
      "risk",
      "protective",
      "change",
      "not-assessed",
      "change",
      "risk",
      "risk",
      "change",
      "not-assessed",
    ],
    exit: [
      "change",
      "protective",
      "protective",
      "risk",
      "change",
      "protective",
      "protective",
      "change",
      "change",
      "protective",
      "protective",
      "protective",
      "not-assessed",
      "change",
      "risk",
      "protective",
      "protective",
      "not-assessed",
    ],
  },
  {
    id: 20,
    household: 5,
    ethnicity: "Pākehā",
    iwi: null,
    location: "Whakatāne",
    service: "He Tohu Kāinga",
    durationBand: "under-6m",
    entry: [
      "change",
      "change",
      "change",
      "risk",
      "change",
      "protective",
      "protective",
      "change",
      "protective",
      "risk",
      "risk",
      "change",
      "change",
      "not-assessed",
      "risk",
      "risk",
      "change",
      "not-assessed",
    ],
    exit: [
      "protective",
      "protective",
      "protective",
      "protective",
      "change",
      "protective",
      "protective",
      "change",
      "protective",
      "protective",
      "protective",
      "protective",
      "change",
      "not-assessed",
      "risk",
      "protective",
      "change",
      "not-assessed",
    ],
  },
  {
    id: 21,
    household: 6,
    ethnicity: "Māori",
    iwi: "Te Whakatōhea",
    location: "Whakatāne",
    service: "Transition to Adulthood",
    durationBand: "6-12m",
    entry: [
      "change",
      "change",
      "change",
      "risk",
      "change",
      "change",
      "protective",
      "change",
      "protective",
      "change",
      "risk",
      "change",
      "change",
      "risk",
      "risk",
      "risk",
      "protective",
      "not-assessed",
    ],
    exit: [
      "protective",
      "protective",
      "protective",
      "protective",
      "change",
      "change",
      "protective",
      "protective",
      "protective",
      "protective",
      "risk",
      "change",
      "protective",
      "protective",
      "risk",
      "change",
      "protective",
      "not-assessed",
    ],
  },
  {
    id: 22,
    household: 5,
    ethnicity: "Māori",
    iwi: "Te Whakatōhea",
    location: "Whakatāne",
    service: "Family Start",
    durationBand: "1-2y",
    entry: [
      "change",
      "change",
      "change",
      "risk",
      "change",
      "protective",
      "protective",
      "change",
      "change",
      "risk",
      "change",
      "protective",
      "risk",
      "change",
      "risk",
      "risk",
      "change",
      "not-assessed",
    ],
    exit: [
      "protective",
      "protective",
      "protective",
      "protective",
      "change",
      "protective",
      "protective",
      "protective",
      "protective",
      "protective",
      "change",
      "protective",
      "risk",
      "change",
      "risk",
      "change",
      "protective",
      "not-assessed",
    ],
  },
  {
    id: 23,
    household: 3,
    ethnicity: "Māori",
    iwi: "Te Whakatōhea",
    location: "Whakatāne",
    service: "Social Workers in Schools",
    durationBand: "over-2y",
    entry: [
      "risk",
      "risk",
      "change",
      "risk",
      "risk",
      "risk",
      "risk",
      "protective",
      "change",
      "risk",
      "change",
      "risk",
      "not-assessed",
      "protective",
      "risk",
      "risk",
      "risk",
      "not-assessed",
    ],
    exit: [
      "change",
      "risk",
      "change",
      "protective",
      "change",
      "change",
      "risk",
      "protective",
      "change",
      "protective",
      "protective",
      "change",
      "not-assessed",
      "protective",
      "risk",
      "change",
      "change",
      "not-assessed",
    ],
  },
  {
    id: 24,
    household: 3,
    ethnicity: "Māori",
    iwi: "Te Whānau-ā-Apanui",
    location: "Kawerau",
    service: "Whānau Resilience",
    durationBand: "under-6m",
    entry: [
      "risk",
      "risk",
      "change",
      "change",
      "change",
      "protective",
      "protective",
      "protective",
      "change",
      "protective",
      "risk",
      "change",
      "not-assessed",
      "change",
      "risk",
      "risk",
      "change",
      "not-assessed",
    ],
    exit: [
      "change",
      "risk",
      "protective",
      "protective",
      "protective",
      "protective",
      "protective",
      "protective",
      "protective",
      "protective",
      "change",
      "change",
      "not-assessed",
      "protective",
      "change",
      "change",
      "change",
      "not-assessed",
    ],
  },
  {
    id: 25,
    household: 5,
    ethnicity: "Māori",
    iwi: "Tūhoe",
    location: "Kawerau",
    service: "Health Navigator",
    durationBand: "6-12m",
    entry: [
      "protective",
      "protective",
      "protective",
      "risk",
      "risk",
      "change",
      "risk",
      "risk",
      "change",
      "risk",
      "change",
      "protective",
      "risk",
      "risk",
      "risk",
      "protective",
      "change",
      "not-assessed",
    ],
    exit: [
      "protective",
      "protective",
      "protective",
      "protective",
      "protective",
      "change",
      "protective",
      "protective",
      "change",
      "protective",
      "change",
      "protective",
      "change",
      "change",
      "change",
      "protective",
      "change",
      "not-assessed",
    ],
  },
  {
    id: 26,
    household: 9,
    ethnicity: "Māori",
    iwi: "Tūhoe",
    location: "Whakatāne",
    service: "Rukuhia Te Mahi",
    durationBand: "1-2y",
    entry: [
      "protective",
      "protective",
      "protective",
      "risk",
      "risk",
      "risk",
      "risk",
      "risk",
      "change",
      "risk",
      "change",
      "protective",
      "not-assessed",
      "not-assessed",
      "risk",
      "protective",
      "risk",
      "not-assessed",
    ],
    exit: [
      "protective",
      "protective",
      "protective",
      "protective",
      "risk",
      "protective",
      "protective",
      "change",
      "change",
      "protective",
      "protective",
      "protective",
      "not-assessed",
      "not-assessed",
      "change",
      "protective",
      "protective",
      "not-assessed",
    ],
  },
  {
    id: 27,
    household: 9,
    ethnicity: "Māori",
    iwi: "Ngāti Porou",
    location: "Whakatāne",
    service: "Tamariki Ora",
    durationBand: "over-2y",
    entry: [
      "risk",
      "risk",
      "protective",
      "risk",
      "protective",
      "change",
      "risk",
      "change",
      "protective",
      "protective",
      "change",
      "risk",
      "change",
      "risk",
      "risk",
      "risk",
      "change",
      "not-assessed",
    ],
    exit: [
      "change",
      "change",
      "protective",
      "protective",
      "protective",
      "protective",
      "protective",
      "protective",
      "protective",
      "protective",
      "change",
      "change",
      "protective",
      "change",
      "change",
      "change",
      "protective",
      "not-assessed",
    ],
  },
  {
    id: 28,
    household: 11,
    ethnicity: "Samoan",
    iwi: null,
    location: "Whakatāne",
    service: "He Tohu Kāinga",
    durationBand: "under-6m",
    entry: [
      "risk",
      "change",
      "risk",
      "risk",
      "protective",
      "protective",
      "risk",
      "change",
      "change",
      "risk",
      "protective",
      "change",
      "risk",
      "change",
      "risk",
      "risk",
      "protective",
      "not-assessed",
    ],
    exit: [
      "change",
      "change",
      "change",
      "protective",
      "protective",
      "protective",
      "protective",
      "change",
      "change",
      "protective",
      "protective",
      "change",
      "change",
      "change",
      "change",
      "protective",
      "protective",
      "not-assessed",
    ],
  },
  {
    id: 29,
    household: 12,
    ethnicity: "Māori",
    iwi: "Ngāti Awa",
    location: "Whakatāne",
    service: "Transition to Adulthood",
    durationBand: "6-12m",
    entry: [
      "risk",
      "risk",
      "change",
      "change",
      "change",
      "protective",
      "risk",
      "risk",
      "risk",
      "risk",
      "change",
      "change",
      "change",
      "not-assessed",
      "risk",
      "protective",
      "change",
      "not-assessed",
    ],
    exit: [
      "protective",
      "change",
      "change",
      "protective",
      "change",
      "protective",
      "protective",
      "change",
      "change",
      "risk",
      "protective",
      "change",
      "change",
      "not-assessed",
      "risk",
      "protective",
      "protective",
      "not-assessed",
    ],
  },
  {
    id: 30,
    household: 4,
    ethnicity: "Māori",
    iwi: "Ngāti Awa",
    location: "Whakatāne",
    service: "Family Start",
    durationBand: "1-2y",
    entry: [
      "risk",
      "risk",
      "change",
      "risk",
      "risk",
      "change",
      "risk",
      "change",
      "risk",
      "risk",
      "risk",
      "protective",
      "risk",
      "not-assessed",
      "risk",
      "change",
      "protective",
      "not-assessed",
    ],
    exit: [
      "protective",
      "change",
      "protective",
      "protective",
      "protective",
      "change",
      "protective",
      "change",
      "protective",
      "risk",
      "change",
      "protective",
      "risk",
      "not-assessed",
      "change",
      "protective",
      "protective",
      "not-assessed",
    ],
  },
  {
    id: 31,
    household: 5,
    ethnicity: "Māori",
    iwi: "Ngāti Awa",
    location: "Whakatāne",
    service: "Social Workers in Schools",
    durationBand: "over-2y",
    entry: [
      "change",
      "change",
      "protective",
      "risk",
      "risk",
      "change",
      "protective",
      "risk",
      "change",
      "risk",
      "risk",
      "change",
      "risk",
      "risk",
      "protective",
      "change",
      "protective",
      "not-assessed",
    ],
    exit: [
      "protective",
      "change",
      "protective",
      "risk",
      "change",
      "protective",
      "protective",
      "change",
      "change",
      "risk",
      "change",
      "change",
      "change",
      "change",
      "protective",
      "protective",
      "protective",
      "not-assessed",
    ],
  },
  {
    id: 32,
    household: 6,
    ethnicity: "Māori",
    iwi: "Ngāti Awa",
    location: "Whakatāne",
    service: "Whānau Resilience",
    durationBand: "under-6m",
    entry: [
      "change",
      "change",
      "protective",
      "change",
      "protective",
      "change",
      "risk",
      "change",
      "risk",
      "risk",
      "change",
      "protective",
      "not-assessed",
      "protective",
      "protective",
      "risk",
      "change",
      "not-assessed",
    ],
    exit: [
      "protective",
      "change",
      "protective",
      "change",
      "protective",
      "change",
      "risk",
      "change",
      "protective",
      "risk",
      "change",
      "protective",
      "not-assessed",
      "protective",
      "protective",
      "protective",
      "change",
      "not-assessed",
    ],
  },
  {
    id: 33,
    household: 7,
    ethnicity: "Māori",
    iwi: "Ngāti Awa",
    location: "Kawerau",
    service: "Health Navigator",
    durationBand: "6-12m",
    entry: [
      "protective",
      "change",
      "change",
      "protective",
      "protective",
      "change",
      "change",
      "change",
      "protective",
      "risk",
      "protective",
      "change",
      "risk",
      "not-assessed",
      "risk",
      "protective",
      "risk",
      "not-assessed",
    ],
    exit: [
      "protective",
      "change",
      "protective",
      "protective",
      "protective",
      "protective",
      "change",
      "protective",
      "protective",
      "risk",
      "protective",
      "change",
      "change",
      "not-assessed",
      "risk",
      "protective",
      "protective",
      "not-assessed",
    ],
  },
  {
    id: 34,
    household: 4,
    ethnicity: "Irish",
    iwi: null,
    location: "Kawerau",
    service: "Rukuhia Te Mahi",
    durationBand: "1-2y",
    entry: [
      "protective",
      "change",
      "change",
      "protective",
      "change",
      "change",
      "change",
      "protective",
      "change",
      "protective",
      "protective",
      "protective",
      "change",
      "not-assessed",
      "risk",
      "change",
      "change",
      "not-assessed",
    ],
    exit: [
      "protective",
      "change",
      "change",
      "protective",
      "protective",
      "protective",
      "change",
      "protective",
      "protective",
      "protective",
      "protective",
      "protective",
      "protective",
      "not-assessed",
      "risk",
      "protective",
      "protective",
      "not-assessed",
    ],
  },
  {
    id: 35,
    household: 5,
    ethnicity: "Māori",
    iwi: "Ngāti Tūwharetoa",
    location: "Kawerau",
    service: "Tamariki Ora",
    durationBand: "over-2y",
    entry: [
      "protective",
      "change",
      "protective",
      "change",
      "protective",
      "protective",
      "change",
      "change",
      "protective",
      "change",
      "protective",
      "change",
      "risk",
      "not-assessed",
      "risk",
      "change",
      "change",
      "not-assessed",
    ],
    exit: [
      "protective",
      "change",
      "protective",
      "change",
      "protective",
      "protective",
      "change",
      "change",
      "protective",
      "protective",
      "protective",
      "protective",
      "change",
      "not-assessed",
      "risk",
      "protective",
      "change",
      "not-assessed",
    ],
  },
  {
    id: 36,
    household: 4,
    ethnicity: "Māori",
    iwi: "Ngāti Tūwharetoa",
    location: "Kawerau",
    service: "He Tohu Kāinga",
    durationBand: "under-6m",
    entry: [
      "change",
      "change",
      "change",
      "change",
      "change",
      "protective",
      "change",
      "protective",
      "risk",
      "change",
      "protective",
      "change",
      "not-assessed",
      "not-assessed",
      "protective",
      "change",
      "risk",
      "not-assessed",
    ],
    exit: [
      "protective",
      "change",
      "change",
      "change",
      "protective",
      "protective",
      "change",
      "protective",
      "risk",
      "change",
      "protective",
      "change",
      "not-assessed",
      "not-assessed",
      "protective",
      "change",
      "change",
      "not-assessed",
    ],
  },
  {
    id: 37,
    household: 6,
    ethnicity: "Māori",
    iwi: "Ngāti Tūwharetoa",
    location: "Kawerau",
    service: "Transition to Adulthood",
    durationBand: "6-12m",
    entry: [
      "change",
      "change",
      "change",
      "risk",
      "protective",
      "protective",
      "risk",
      "protective",
      "change",
      "protective",
      "change",
      "protective",
      "not-assessed",
      "not-assessed",
      "risk",
      "risk",
      "risk",
      "not-assessed",
    ],
    exit: [
      "protective",
      "change",
      "protective",
      "risk",
      "protective",
      "protective",
      "risk",
      "protective",
      "change",
      "protective",
      "protective",
      "protective",
      "not-assessed",
      "not-assessed",
      "risk",
      "risk",
      "change",
      "not-assessed",
    ],
  },
  {
    id: 38,
    household: 3,
    ethnicity: "Pākehā",
    iwi: null,
    location: "Whakatāne",
    service: "Family Start",
    durationBand: "1-2y",
    entry: [
      "risk",
      "change",
      "protective",
      "change",
      "change",
      "change",
      "risk",
      "change",
      "change",
      "change",
      "change",
      "change",
      "risk",
      "not-assessed",
      "protective",
      "change",
      "change",
      "not-assessed",
    ],
    exit: [
      "protective",
      "change",
      "protective",
      "change",
      "change",
      "protective",
      "risk",
      "protective",
      "change",
      "change",
      "change",
      "change",
      "change",
      "not-assessed",
      "protective",
      "change",
      "change",
      "not-assessed",
    ],
  },
  {
    id: 39,
    household: 4,
    ethnicity: "Pākehā",
    iwi: null,
    location: "Whakatāne",
    service: "Social Workers in Schools",
    durationBand: "over-2y",
    entry: [
      "risk",
      "risk",
      "change",
      "protective",
      "change",
      "risk",
      "change",
      "protective",
      "change",
      "change",
      "risk",
      "risk",
      "risk",
      "not-assessed",
      "change",
      "change",
      "risk",
      "not-assessed",
    ],
    exit: [
      "protective",
      "protective",
      "protective",
      "protective",
      "protective",
      "change",
      "change",
      "protective",
      "change",
      "change",
      "risk",
      "change",
      "change",
      "not-assessed",
      "change",
      "change",
      "change",
      "not-assessed",
    ],
  },
  {
    id: 40,
    household: 3,
    ethnicity: "Māori",
    iwi: "Ngāti Awa",
    location: "Whakatāne",
    service: "Whānau Resilience",
    durationBand: "under-6m",
    entry: [
      "risk",
      "risk",
      "change",
      "change",
      "protective",
      "risk",
      "protective",
      "change",
      "protective",
      "risk",
      "protective",
      "risk",
      "not-assessed",
      "not-assessed",
      "protective",
      "risk",
      "risk",
      "not-assessed",
    ],
    exit: [
      "protective",
      "risk",
      "protective",
      "change",
      "protective",
      "change",
      "protective",
      "change",
      "protective",
      "risk",
      "protective",
      "risk",
      "not-assessed",
      "not-assessed",
      "protective",
      "risk",
      "change",
      "not-assessed",
    ],
  },
];

function toRecord(states: FactorStateKind[]): Record<string, FactorStateKind> {
  const out: Record<string, FactorStateKind> = {};
  MOCK_SUBDOMAIN_ORDER.forEach((id, i) => {
    out[id] = states[i] ?? "not-assessed";
  });
  return out;
}

/** The mock dataset as clean ReportProfile records (entry/exit keyed by subdomain id). */
export const mockReportProfiles: ReportProfile[] = ROWS.map((r) => ({
  id: r.id,
  household: r.household,
  ethnicity: r.ethnicity,
  iwi: r.iwi,
  location: r.location,
  service: r.service,
  durationBand: r.durationBand,
  entry: toRecord(r.entry),
  exit: toRecord(r.exit),
}));

export default mockReportProfiles;
